import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { resolvePhotoUrl } from "@/lib/img";
import {
  ShieldCheck, ArrowLeft, WhatsappLogo, CheckCircle, Clock
} from "@phosphor-icons/react";
import { waLink } from "@/lib/contacts";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const STAGE_LABEL = {
  submitted: "Submitted",
  under_review: "Under Review",
  offer_sent: "Offer Sent",
  negotiating: "Negotiation In Progress",
  inspection_scheduled: "Inspection Scheduled",
  inspection_completed: "Inspection Completed",
  final_offer_sent: "Final Offer Ready",
  deal_confirmed: "Deal Confirmed",
  closed: "Closed",
  follow_up: "Follow Up Later",
};

const PIPELINE = [
  { key: "submitted", label: "Submitted" },
  { key: "offer_sent", label: "Offer" },
  { key: "negotiating", label: "Negotiation" },
  { key: "inspection_scheduled", label: "Inspection" },
  { key: "deal_confirmed", label: "Closed" },
];

const STATUS_TO_STAGE_IDX = {
  submitted: 0, under_review: 0, follow_up: 0,
  offer_sent: 1,
  negotiating: 2,
  inspection_scheduled: 3, inspection_completed: 3, final_offer_sent: 3,
  deal_confirmed: 4, closed: 4,
};

export default function LiveOffers() {
  const { vid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  const tickRef = useRef(null);

  const load = async () => {
    try {
      const { data: next } = await api.get(`/offers/${vid}`);
      setData((prev) => {
        const prevAmt = prev?.vehicle?.current_admin_offer || 0;
        const nextAmt = next?.vehicle?.current_admin_offer || 0;
        if (prev && nextAmt > prevAmt) {
          setPulse(true);
          setTimeout(() => setPulse(false), 600);
        }
        return next;
      });
    } catch (err) { console.error("Acquisition status load failed:", err); }
  };

  useEffect(() => {
    load();
    let timer = null;
    const start = () => { if (!timer) timer = setInterval(load, 10000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => {
      if (document.hidden) stop();
      else { load(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    tickRef.current = { stop };
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="text-white/40 text-xs tracking-widest">LOADING…</div></div>;

  const v = data.vehicle;
  const currentOffer = v.current_admin_offer || 0;
  const stageIdx = STATUS_TO_STAGE_IDX[v.status] ?? 0;
  const sellerLabel = STAGE_LABEL[v.status] || v.status;

  // History of dealership offers only (filter out legacy multi-buyer entries from old DB rows).
  const offerHistory = (data.offers || []).filter(o => o.kind === "dealership_offer" || o.buyer_label === "Q Drives Dealership");

  return (
    <div className="min-h-screen pb-32" data-testid="live-offers-page">
      <header className="px-6 pb-2 qd-pt-safe-header-lg flex items-center justify-between">
        <button data-testid="offers-back" onClick={() => nav("/activity")} className="text-white/60 hover:text-white flex items-center gap-1.5 text-xs tracking-wide">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#22C55E]" />
          <span className="text-[10px] tracking-[0.3em] text-white/55 uppercase">Live</span>
        </div>
      </header>

      <div className="px-6 pt-6 max-w-3xl mx-auto">
        {/* Vehicle header */}
        <div className="qd-card overflow-hidden mb-5">
          {v.photos && v.photos.length > 0 && (
            <div className="w-full h-44 sm:h-56 bg-[#0E0E0E] overflow-hidden border-b border-[#1A1A1A]" data-testid="lo-vehicle-hero">
              <img
                src={resolvePhotoUrl(v.photos[0])}
                alt={`${v.make} ${v.model}`}
                className="w-full h-full object-cover"
                loading="eager"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
            </div>
          )}
          <div className="p-6">
            <div className="qd-overline mb-2">Acquisition Status</div>
            <h1 className="qd-h1 text-3xl text-white">{v.make} {v.model}</h1>
            <div className="text-[11px] tracking-widest text-white/40 mt-2">
              {v.registration_number} · {v.year} · {v.fuel_type} · {v.transmission}
            </div>

            <div className="mt-5 flex items-baseline justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] tracking-widest text-white/40 uppercase">Dealership Offer</div>
                <div className={`font-display text-3xl sm:text-4xl font-extrabold mt-1 transition-colors duration-300 ${pulse ? "text-[#FF1E1E]" : currentOffer ? "text-[#22C55E]" : "text-white/30"}`}
                     data-testid="dealership-offer-amount">
                  {currentOffer ? `₹${(currentOffer / 100000).toFixed(2)}L` : "Awaiting"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest text-white/40 uppercase">Your Expected</div>
                <div className="font-display text-lg text-white/85 mt-1">
                  ₹{((v.expected_price || 0) / 100000).toFixed(2)}L
                </div>
              </div>
            </div>

            {/* Operational stage indicator */}
            <div className="mt-5 px-3 py-2 border border-[#1F1F1F] bg-[#0E0E0E] flex items-center justify-between gap-3" data-testid="seller-stage-pill">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF1E1E]" />
                <span className="text-[12px] text-white">{sellerLabel}</span>
              </div>
              {v.last_contacted_at && (
                <span className="text-[10px] text-white/45 tracking-wide flex items-center gap-1">
                  <Clock size={11} weight="bold" /> {timeAgo(v.last_contacted_at)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pipeline tracker */}
        <div className="qd-card p-5 mb-5">
          <div className="qd-overline mb-4">Pipeline</div>
          <div className="flex items-center gap-1.5">
            {PIPELINE.map((stage, i) => {
              const reached = i <= stageIdx;
              return (
                <div key={stage.key} className="flex-1 min-w-0" data-testid={`pipeline-stage-${stage.key}`}>
                  <div className="h-[3px]" style={{ background: reached ? "#C20505" : "rgba(255,255,255,0.08)" }} />
                  <div className={`text-[10px] mt-1.5 truncate ${reached ? "text-white/85" : "text-white/35"}`}>{stage.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Seller response CTAs */}
        {currentOffer > 0 && v.status !== "deal_confirmed" && v.status !== "closed" && (
          <RespondPanel v={v} onAction={load} />
        )}

        {/* Dealership offer history (real, persisted) */}
        {offerHistory.length > 0 && (
          <div className="qd-card overflow-hidden mb-5" data-testid="offer-history">
            <div className="px-4 py-3 border-b border-[#1F1F1F]">
              <div className="qd-overline">Offer History</div>
            </div>
            <div className="divide-y divide-[#1A1A1A]">
              {offerHistory.map((o, i) => (
                <div key={o.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#22C55E]" />
                    <span className="text-sm text-white">₹{(o.amount / 100000).toFixed(2)}L</span>
                  </div>
                  <span className="text-[11px] text-white/45">{timeAgo(o.created_at)}{i === 0 ? " · current" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operational reassurance — single dealership counterparty */}
        <div className="qd-card p-5 flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#FF1E1E] mt-0.5" weight="fill" />
          <div className="text-xs text-white/65 flex-1">
            Q Drives is your direct acquisition counterparty. Your specialist is managing this negotiation 1-to-1 — no third-party buyers, no marketplace bidding.
          </div>
        </div>

        <a
          href={waLink(`Hi Q Drives, I'd like to discuss the acquisition of my ${v.make} ${v.model} (${v.registration_number}).`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-specialist-btn"
          className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2"
        >
          <WhatsappLogo size={14} weight="fill" /> Contact Acquisition Specialist
        </a>
      </div>
    </div>
  );
}


function RespondPanel({ v, onAction }) {
  const [mode, setMode] = useState("idle");
  const [counter, setCounter] = useState("");
  const [note, setNote] = useState("");
  const fmt = (n) => `₹${(n / 100000).toFixed(2)}L`;

  const accept = async () => {
    if (!window.confirm(`Accept the dealership offer of ${fmt(v.current_admin_offer)}?`)) return;
    setMode("submitting");
    try {
      await api.post(`/vehicles/${v.id}/respond-offer`, { action: "accept" });
      onAction();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to accept offer");
      setMode("idle");
    }
  };

  const submitCounter = async () => {
    const n = parseInt(counter.replace(/\D/g, ""), 10);
    if (!n || n <= 0) { alert("Enter a valid amount"); return; }
    setMode("submitting");
    try {
      await api.post(`/vehicles/${v.id}/respond-offer`, {
        action: "counter",
        counter_amount: n,
        note: note.trim() || null,
      });
      setMode("idle");
      setCounter("");
      setNote("");
      onAction();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to submit");
      setMode("idle");
    }
  };

  if (v.seller_response?.action === "accept") {
    return (
      <div className="qd-card p-5 mb-5 border-l-2 border-[#22C55E]" data-testid="response-recorded">
        <div className="qd-overline mb-1" style={{ color: "#22C55E" }}>Acceptance Recorded</div>
        <div className="text-sm text-white/75">
          Our acquisition specialist will reach out to finalize paperwork and inspection.
        </div>
      </div>
    );
  }

  if (mode === "counter") {
    return (
      <div className="qd-card p-5 mb-5" data-testid="respond-panel-counter">
        <div className="qd-overline mb-3">Request Higher Offer</div>
        <input
          data-testid="lo-counter-amount"
          value={counter}
          onChange={(e) => setCounter(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") submitCounter(); }}
          inputMode="numeric"
          placeholder={`Your asking price (current ${fmt(v.current_admin_offer)})`}
          className="qd-input-block mb-3"
        />
        <textarea
          data-testid="lo-counter-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for the dealership team"
          className="qd-input-block min-h-[70px] mb-3"
        />
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="lo-counter-submit" onClick={submitCounter} disabled={mode === "submitting"} className="btn-primary text-xs py-2">
            {mode === "submitting" ? "Sending…" : "Send Request"}
          </button>
          <button data-testid="lo-counter-cancel" onClick={() => { setMode("idle"); setCounter(""); }} className="btn-secondary text-xs py-2">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qd-card p-5 mb-5" data-testid="respond-panel-idle">
      <div className="qd-overline mb-3">Respond to Dealership Offer</div>
      {v.seller_response?.action === "counter" && (
        <div className="text-[11px] text-white/55 mb-3">
          Your previous request: <span className="text-white">{fmt(v.seller_response.amount)}</span> · {timeAgo(v.seller_response.at)}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="lo-accept-offer" onClick={accept} disabled={mode === "submitting"} className="btn-primary text-xs py-3">
          Accept Offer
        </button>
        <button data-testid="lo-request-higher" onClick={() => setMode("counter")} disabled={mode === "submitting"} className="btn-secondary text-xs py-3">
          Request Higher Offer
        </button>
      </div>
    </div>
  );
}
